import { useEffect, useMemo, useState } from 'react';
import { Form, Select, Col, Row, Skeleton, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { notification } from '@ohpen/enso-ui';
import { ImportColumnMapping, ImportSchemaColumn } from '@pmp/api';

import styles from '../styles.module.css';
import { HeaderMappingProps, Mapping } from '../interfaces';

const { Option } = Select;

const { Title } = Typography;

type KeyValuePair = { [key: string]: string };

export const HeaderMapping = ({ importId, importEntity, form, headers, schema, columns }: HeaderMappingProps) => {
  const [selected, setSelected] = useState<string[] | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const initialMapping: Mapping = {};

    const findHeaderMatch = (field: ImportSchemaColumn, headers: string[]): string | undefined => {
      const lowerDefaultKey = field.defaultClientKey.toLowerCase();
      // Accept canonical property keys too (e.g. `initialRateType`) so exports
      // that emit the column key round-trip cleanly on re-import — without
      // this, a CSV using `initialRateType` wouldn't auto-map to the
      // `InitialRateTypeId` default client key.
      const lowerCanonicalKey = field.key.toLowerCase();
      const customKeys = field.customClientKeys?.map(key => key.toLowerCase()) || [];
      return headers.find(header => {
        const h = header?.toLowerCase();
        return h === lowerDefaultKey || h === lowerCanonicalKey || customKeys.includes(h);
      });
    };

    const setMappingFromSchema = () => {
      schema.forEach((field: ImportSchemaColumn) => {
        initialMapping[field.key] = findHeaderMatch(field, headers);
      });
    };

    const setMappingFromColumns = () => {
      columns.forEach((column: ImportColumnMapping) => {
        initialMapping[column.key] = column.clientKey;
      });
    };

    if (headers.length) {
      if (!columns.length) {
        setMappingFromSchema();
      } else {
        setMappingFromColumns();
      }
      form.setFieldsValue(initialMapping);
      setSelected(Object.values(initialMapping).filter((value): value is string => !!value));
    } else if (importId) {
      setMappingFromColumns();
      form.setFieldsValue(initialMapping);
      setSelected(Object.values(initialMapping).filter((value): value is string => !!value));
    } else {
      notification().error({ title: t('global.errors.unexpected') });
    }
  }, [schema, headers, form, importId, t, columns]);

  const handleValuesChange = (changedValues: Partial<KeyValuePair>, allValues: KeyValuePair) => {
    setSelected(Object.values(allValues).filter(Boolean));
  };

  const formItems = useMemo(
    () =>
      schema.length && selected
        ? schema.map((field: ImportSchemaColumn) => (
            <Form.Item
              key={field.key}
              name={field.key}
              label={field.label}
              // label={field.type === t(`pmp.${actionEntityTypeToTranslationKey[importEntity]}.labels.${field.key}`)} TODO: Find a way to get labels for Additional Fields
              rules={[{ required: field.required, message: t('global.errors.required') }]}
            >
              <Select placeholder={t('global.labels.selectSpreadsheetHeader')} allowClear>
                {headers.map((header: string) => (
                  <Option key={header} value={header} disabled={selected.includes(header)}>
                    {header}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          ))
        : Array.from({ length: 10 }).map((_, rowIndex) => (
            <Row key={`row-${rowIndex}`} gutter={16} className={styles.skeletonRow}>
              <Col key="col-1" flex={1} className={styles.skeletonLabelCol}>
                <Skeleton.Input block active size="default" />
              </Col>
              <Col key="col-2" flex={1} className={styles.skeletonControlCol}>
                <Skeleton.Input block active size="default" />
              </Col>
            </Row>
          )),
    [schema, selected, t, headers],
  );

  return (
    <div className={styles.formContainer}>
      <div className={styles.titlesContainer}>
        <Title level={5}>{t('global.labels.XColumnName', { x: t(`pmp.entities.${importEntity}`) })}:</Title>
        <Title level={5}>{t('global.labels.importedFileHeader')}:</Title>
      </div>
      <Form form={form} onValuesChange={handleValuesChange}>
        {formItems}
      </Form>
    </div>
  );
};

export default HeaderMapping;
